import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProvisionItem } from '../../models/provisionitem';
import { ProvisionChange } from '../../models/provisionchange';
import { ProposerExecution } from '../../models/proposerexecution';
import { ExecutionService } from '../../services/execution/execution.service';
import { ErrorService } from '../../services/error/error.service';
import { ProvisionService } from '../../services/provision/provision.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'conversion-selectconfiguration',
  templateUrl: './selectconfiguration.component.html',
  styleUrls: ['./selectconfiguration.component.css']
})
export class SelectConfigurationComponent implements OnChanges, OnInit, OnDestroy {
  @Input() fpcfamily: string;
  @Input() fpcexecution: string;
  @Input() type: string;
  @Input() selectonce = false;
  @Input() activeComponent = true;
  @Input() changes: ProvisionChange[] = [];
  @Input() proposerexecutions: ProposerExecution[];
  @Output() selectedexecution = new EventEmitter<ProvisionItem>();
  @ViewChild('dropdown', { read: ElementRef }) dropdown: ElementRef<HTMLElement & { close?: () => Promise<void> }>;

  executions: ProvisionItem[] | null = null;
  loadingexecutions = false;
  loadFailed = false;
  selectedValue: string | null = null;
  executionDisplayText = 'Select configuration';
  private selectedOption: ProvisionItem | null = null;
  private activeRequest: Subscription;

  constructor(private executionService: ExecutionService,
              private errorService: ErrorService,
              private provisionService: ProvisionService) {}

  get readOnly(): boolean {
    const hasChange = this.changes?.some(item => item.Family === this.fpcfamily && item.Type === this.type);
    return (this.selectonce && hasChange) || (this.selectedValue !== null && !this.activeComponent);
  }

  get displayedExecutions(): ProvisionItem[] {
    const options = this.executions || [];
    // Tegel reads the selected label from its option, even when the dropdown is closed.
    return this.selectedOption && !options.some(item => item.Execution === this.selectedValue)
      ? [this.selectedOption, ...options] : options;
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fpcfamily || changes.fpcexecution || changes.type) { this.clearExecutions(); }
    const change = this.changes?.find(item => item.Family === this.fpcfamily && item.Type === this.type);
    this.selectedValue = change?.ToExecution || null;
    this.executionDisplayText = this.selectedValue
      ? change.ToExecutionDescription?.trim() || this.selectedValue
      : change ? 'Removed' : 'Select configuration';
    this.selectedOption = this.selectedValue
      ? new ProvisionItem(this.type, this.fpcfamily, change.FamilyDescription, this.selectedValue, change.ToExecutionDescription)
      : null;
  }

  onKeydown(event: KeyboardEvent): void {
    if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) { this.getExecutions(); }
  }

  getExecutions(): void {
    if (this.readOnly || this.loadingexecutions || this.executions !== null) { return; }
    this.errorService.insertError(null);
    this.loadingexecutions = true;
    this.loadFailed = false;
    // Match master: fetch on opening, with no proposer executions and no client cache.
    this.activeRequest = this.executionService.getExecutions(this.fpcfamily, []).subscribe({
      next: items => {
        this.loadingexecutions = false;
        // Avoid master's splice(-1) bug when OAS does not return the original execution.
        this.executions = items.filter(item => item.Type === 'FPC' && item.Execution !== this.fpcexecution)
          .sort((a, b) => Number(b.Recommended === true) - Number(a.Recommended === true) || a.Execution.localeCompare(b.Execution));
      },
      error: error => {
        this.loadingexecutions = false;
        this.loadFailed = true;
        this.errorService.insertError('Available values could not be loaded. ' + error);
      }
    });
  }

  selectExecution(value: string): void {
    if (this.readOnly) { return; }
    const item = this.executions?.find(option => option.Execution === value);
    if (!item) { return; }
    this.selectedValue = value;
    this.selectedOption = item;
    this.executionDisplayText = this.optionLabel(item);
    this.clearExecutions();
    this.selectedexecution.emit(item);
  }

  optionLabel(item: ProvisionItem): string { return item.ExecutionDescription?.trim() || item.Execution; }
  trackExecution(_index: number, item: ProvisionItem): string { return item.Execution; }

  clearExecutions(): void {
    this.activeRequest?.unsubscribe();
    this.loadingexecutions = false;
    this.loadFailed = false;
    this.executions = null;
    void this.dropdown?.nativeElement.close?.();
  }

  ngOnDestroy(): void {
    this.activeRequest?.unsubscribe();
  }
}
